import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { productUrl } from '../lib/productUrl';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { isFlatPriced } from '../lib/saleItems';
import { useCart } from '../contexts/CartContext';
import { ShoppingCart, Plus, Minus } from 'lucide-react';

const ProductCard = ({ product, compact }) => {
  const { addItem, updateQuantity, cart } = useCart();
  const { t } = useTranslation();
  const [flyAnim, setFlyAnim] = useState(false);
  const [toast, setToast] = useState(null);
  const btnRef = useRef(null);
  const qtyRef = useRef(null);

  const cartItem = cart.items.find(i => i.product_id === product.id && !i.is_cross_sell);
  const qty = cartItem?.quantity || 0;

  const triggerFlyAnimation = useCallback((sourceEl) => {
    const cartIcon = document.querySelector('[data-testid="cart-link"]');
    if (!cartIcon || !sourceEl) return;

    const srcRect = sourceEl.getBoundingClientRect();
    const cartRect = cartIcon.getBoundingClientRect();

    const dot = document.createElement('div');
    dot.style.cssText = `
      position: fixed; z-index: 9999; pointer-events: none;
      width: 18px; height: 18px; border-radius: 50%;
      background: var(--primary, #2563eb);
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      left: ${srcRect.left + srcRect.width / 2 - 9}px;
      top: ${srcRect.top + srcRect.height / 2 - 9}px;
      transition: none;
    `;
    document.body.appendChild(dot);

    requestAnimationFrame(() => {
      const dx = cartRect.left + cartRect.width / 2 - 6 - (srcRect.left + srcRect.width / 2 - 9);
      const dy = cartRect.top + cartRect.height / 2 - 6 - (srcRect.top + srcRect.height / 2 - 9);
      dot.style.transition = 'transform 0.6s cubic-bezier(0.22, 0.68, 0.35, 1.2), opacity 0.6s ease';
      dot.style.transform = `translate(${dx}px, ${dy}px) scale(0.5)`;
      dot.style.opacity = '0.5';
    });

    setTimeout(() => {
      dot.remove();
      cartIcon.classList.add('cart-bounce');
      setTimeout(() => cartIcon.classList.remove('cart-bounce'), 450);
    }, 620);
  }, []);

  const showToast = useCallback((newQty) => {
    setToast(newQty);
    setTimeout(() => setToast(null), 1800);
  }, []);

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    triggerFlyAnimation(btnRef.current);
    setFlyAnim(true);
    setTimeout(() => setFlyAnim(false), 600);
    addItem(product);
    showToast(qty + 1);
  };

  const handleIncrease = (e) => {
    e.preventDefault();
    e.stopPropagation();
    triggerFlyAnimation(qtyRef.current);
    addItem(product);
    showToast(qty + 1);
  };

  const handleDecrease = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (qty - 1 > 0) {
      showToast(qty - 1);
    } else {
      showToast(0);
    }
    updateQuantity(product.id, qty - 1);
  };

  const h = compact ? 'h-8' : 'h-9';

  return (
    <Link to={productUrl(product)} data-testid={`product-card-${product.id}`}>
      <Card className="group border border-border rounded-none hover:shadow-md transition-all duration-300 h-full">
        <CardContent className="p-0 flex flex-col h-full">
          <div className="aspect-square overflow-hidden relative">
            <img
              src={product.image_url || 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=400'}
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
            {/* Toast notification */}
            {toast !== null && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 bg-black/90 text-white text-xs font-semibold px-4 py-2 rounded shadow-lg animate-toast-pop whitespace-nowrap" data-testid={`toast-${product.id}`}>
                {toast === 0 ? t('product.removed') : `${toast}x ${t('product.added')}`}
              </div>
            )}
          </div>

          <div className={`${compact ? 'p-2.5' : 'p-4'} flex flex-col flex-1`}>
            <h3 className={`font-semibold ${compact ? 'text-xs' : 'text-sm'} line-clamp-2 mb-1.5 group-hover:text-primary transition-colors`}>
              {product.name}
            </h3>

            <div className="mt-auto">
              <div className="mb-2">
                {product.daily_price > 0 ? (
                  <p className={`${compact ? 'text-sm' : 'text-lg'} font-bold text-primary`}>
                    {product.daily_price?.toFixed(2)} €
                    <span className={`${compact ? 'text-[10px]' : 'text-xs'} font-normal text-muted-foreground`}>
                      {' '}{isFlatPriced(product)
                        ? 'HT'
                        : (product.price_unit || t('common.per_day')).replace(/^€\s*/, '')}
                    </span>
                  </p>
                ) : (
                  <p className="text-xs font-medium text-muted-foreground">{t('product.quote_only')}</p>
                )}
              </div>

              {qty === 0 ? (
                <Button
                  ref={btnRef}
                  size="sm"
                  className={`rounded-none w-full min-w-0 px-1.5 sm:px-2 bg-black text-white hover:bg-primary transition-all duration-200 ${h} ${compact ? 'text-[11px]' : 'text-xs sm:text-sm'} ${flyAnim ? 'scale-95 opacity-80' : ''}`}
                  onClick={handleAddToCart}
                  data-testid={`add-to-cart-${product.id}`}
                >
                  <ShoppingCart className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} mr-1 sm:mr-1.5 flex-shrink-0`} />
                  <span className="truncate">{t('catalogue.add_to_cart')}</span>
                </Button>
              ) : (
                <div ref={qtyRef} className={`flex items-center w-full border border-primary ${h}`} data-testid={`qty-control-${product.id}`}>
                  <button
                    onClick={handleDecrease}
                    className={`${h} px-2.5 flex items-center justify-center hover:bg-red-50 hover:text-red-600 transition-colors`}
                    data-testid={`decrease-${product.id}`}
                  >
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <span className={`flex-1 text-center font-bold ${compact ? 'text-xs' : 'text-sm'}`} data-testid={`qty-${product.id}`}>
                    {qty}
                  </span>
                  <button
                    onClick={handleIncrease}
                    className={`${h} px-2.5 flex items-center justify-center hover:bg-primary/10 hover:text-primary transition-colors`}
                    data-testid={`increase-${product.id}`}
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default ProductCard;
